import { SelfServiceClient } from '@/insession/SelfService/SelfServiceClient';
import { HeartBeat } from '@/insession/HeartBeat/HeartBeat';
import {
  ISelfServiceClient,
  IHeartBeat,
  IReportHeartbeatTask,
  IRegion,
} from '@amzn/work-spaces-web-client-core-lib';
import { CoreFactory } from '@bridge/factory/CoreFactory';
import { ReportHeartbeatTask } from '@/insession/HeartBeat/ReportHeartbeatTask';
import { PreSessionFactory } from '@bridge/factory/PreSessionFactory';
import { WebLogUploader } from '@amzn/euc-web-log-uploader';
import {
  LogFileType,
  Region,
} from '@amzn/euc-web-log-uploader/dist/types/LogUploaderTypes';

const WSP_LOG_UPLOAD_BATCH_SIZE_BYTES = 500_000;

export interface WspLogUploaderOptions {
  isLogUploaderEnabledByUser: boolean;
  isLogUploaderEnabledAtDirectoryLevel: boolean;
  useDualStackIpv6?: boolean;
}

interface WspLogUploaderTarget {
  orgName: string;
  regionEndpoint: string;
  useDualStackIpv6: boolean;
}

export class InSessionFactory {
  private static selfServiceClient: ISelfServiceClient;
  private static heartBeat: IHeartBeat;
  private static reportHeartbeatTask: IReportHeartbeatTask;
  private static wspLogUploader: WebLogUploader | undefined;
  private static wspLogUploaderTarget: WspLogUploaderTarget | undefined;

  static getSelfServiceClient(): ISelfServiceClient {
    if (!this.selfServiceClient) {
      this.selfServiceClient = new SelfServiceClient(
        CoreFactory.getWsBrokerClient(),
        CoreFactory.getSessionManager(),
        PreSessionFactory.getSessionProvisionModule(),
        CoreFactory.getLogger()
      );
    }

    return this.selfServiceClient;
  }

  static getHeartBeat(): IHeartBeat {
    if (!this.heartBeat) {
      this.heartBeat = new HeartBeat(
        CoreFactory.getWsBrokerClient(),
        CoreFactory.getSessionManager(),
        CoreFactory.getLogger(),
        CoreFactory.getMetrics(),
        CoreFactory.getHeartBeatCore()
      );
    }

    return this.heartBeat;
  }

  static getReportHeartbeatTask(): IReportHeartbeatTask {
    if (!this.reportHeartbeatTask) {
      this.reportHeartbeatTask = new ReportHeartbeatTask({
        heartbeat: this.getHeartBeat(),
        logger: CoreFactory.getLogger(),
        sessionManager: CoreFactory.getSessionManager(),
      });
    }

    return this.reportHeartbeatTask;
  }

  static getWspLogUploader(
    options: WspLogUploaderOptions
  ): WebLogUploader | undefined {
    const logger = CoreFactory.getLogger();
    const device = CoreFactory.getDevice();
    const sessionManager = CoreFactory.getSessionManager();

    const region = sessionManager.get('region') as IRegion;
    const regionEndpoint = region?.endpoint;
    const deviceId = device.getDeviceUUID();
    const orgName = sessionManager.get('domainProperties')?.OrganizationName;
    const useDualStackIpv6 = options.useDualStackIpv6 ?? false;

    const isEnabled =
      logger.isLogUploaderSupported(regionEndpoint) &&
      options.isLogUploaderEnabledByUser &&
      options.isLogUploaderEnabledAtDirectoryLevel;

    if (!isEnabled || !deviceId || !orgName || !regionEndpoint) {
      // Keep the instance so a later enable resumes it rather than creating a
      // second one. Pausing is best effort (FIXME in dependency library).
      if (this.wspLogUploader && this.wspLogUploaderTarget) {
        this.wspLogUploader.flush?.();
        this.wspLogUploader.pauseUpload?.();
        this.wspLogUploaderTarget = undefined;
      }
      return undefined;
    }

    try {
      if (!this.wspLogUploader) {
        const uploader = new WebLogUploader({
          applicationName: 'WSPWebClient',
          deviceId,
          logFileType: LogFileType.wsp,
        });
        uploader.setConfig({
          uploadBatchSizeInBytes: WSP_LOG_UPLOAD_BATCH_SIZE_BYTES,
          useDualStackIpv6,
        });
        uploader.startUpload(orgName, regionEndpoint as Region);
        this.wspLogUploader = uploader;
      } else {
        const current = this.wspLogUploaderTarget;
        if (current?.useDualStackIpv6 !== useDualStackIpv6) {
          this.wspLogUploader.setConfig({ useDualStackIpv6 });
        }
        if (
          current?.orgName !== orgName ||
          current?.regionEndpoint !== regionEndpoint
        ) {
          if (current) {
            this.wspLogUploader.flush?.();
          }
          this.wspLogUploader.startUpload(orgName, regionEndpoint as Region);
        }
      }
      this.wspLogUploaderTarget = { orgName, regionEndpoint, useDualStackIpv6 };

      return this.wspLogUploader;
    } catch (error) {
      this.wspLogUploader?.pauseUpload?.();
      this.wspLogUploaderTarget = undefined;
      logger.error(
        'Failed to initialize WSP log uploader; continuing session without log upload',
        error instanceof Error ? error.message : String(error),
        error instanceof Error ? error.stack : undefined
      );
      return undefined;
    }
  }
}
